import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Invitation } from "@/types/team";
import { useResendInvitation } from "@/hooks/useResendInvitation";
import { InvitationActionsMenu } from "./InvitationActionsMenu";
import { useAuth } from "@/hooks/useAuth";
import { canManageTeam } from "@/lib/permissions";

interface InvitationsTableProps {
  invitations: Invitation[];
  isLoading?: boolean;
}

function StatusBadge({ isExpired }: { isExpired: boolean }) {
  if (isExpired) {
    return (
      <Badge variant="outline" className="text-muted-foreground">
        Expired
      </Badge>
    );
  }
  return <Badge variant="default">Active</Badge>;
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="rounded-full bg-muted p-3 mb-4">
        <svg
          className="h-6 w-6 text-muted-foreground"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      </div>
      <h3 className="font-semibold mb-1">No pending invitations</h3>
      <p className="text-sm text-muted-foreground max-w-sm">
        Invite team members to collaborate on your project.
      </p>
    </div>
  );
}

function LoadingSkeleton({
  hasManagePermission,
}: {
  hasManagePermission: boolean;
}) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[200px]">Email</TableHead>
            <TableHead className="min-w-[100px]">Role</TableHead>
            <TableHead className="min-w-[150px] hidden sm:table-cell">
              Invited By
            </TableHead>
            <TableHead className="min-w-[80px]">Status</TableHead>
            <TableHead className="min-w-[120px] hidden md:table-cell">
              Expires
            </TableHead>
            {hasManagePermission && (
              <TableHead className="min-w-[80px]">Actions</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...Array(5)].map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-4 w-[180px]" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-[60px]" />
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                <Skeleton className="h-4 w-[140px]" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-[60px] rounded-full" />
              </TableCell>
              <TableCell className="hidden md:table-cell">
                <Skeleton className="h-4 w-[100px]" />
              </TableCell>
              {hasManagePermission && (
                <TableCell>
                  <Skeleton className="h-8 w-8" />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function InvitationsTable({
  invitations,
  isLoading,
}: InvitationsTableProps) {
  const { resendInvitation, isResending } = useResendInvitation();
  const { role } = useAuth();
  const hasManagePermission = canManageTeam(role);

  if (isLoading) {
    return <LoadingSkeleton hasManagePermission={hasManagePermission} />;
  }

  if (!invitations || invitations.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[200px]">Email</TableHead>
            <TableHead className="min-w-[100px]">Role</TableHead>
            <TableHead className="min-w-[150px] hidden sm:table-cell">
              Invited By
            </TableHead>
            <TableHead className="min-w-[80px]">Status</TableHead>
            <TableHead className="min-w-[120px] hidden md:table-cell">
              Expires
            </TableHead>
            {hasManagePermission && (
              <TableHead className="min-w-[80px]">Actions</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {invitations.map((invitation) => (
            <TableRow key={invitation.id}>
              <TableCell className="font-mono text-sm">
                {invitation.email}
              </TableCell>
              <TableCell className="capitalize">{invitation.role}</TableCell>
              <TableCell className="text-muted-foreground hidden sm:table-cell font-mono text-sm">
                {invitation.invited_by_email}
              </TableCell>
              <TableCell>
                <StatusBadge isExpired={invitation.is_expired} />
              </TableCell>
              <TableCell className="text-muted-foreground hidden md:table-cell font-mono text-sm">
                {invitation.resent_at
                  ? `Resent ${new Date(invitation.resent_at).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      },
                    )}`
                  : new Date(invitation.expires_at).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      },
                    )}
              </TableCell>
              {hasManagePermission && (
                <TableCell>
                  <InvitationActionsMenu
                    invitation={invitation}
                    isResending={isResending(invitation.id)}
                    onResend={resendInvitation}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
